import { useMemo } from 'react'
import { Area, AreaChart, CartesianGrid, ReferenceLine, XAxis, YAxis } from 'recharts'

import { ExportButtonV2 } from '@/catalog-apps/components/component-library/DataTableV2/components/ExportButtonV2'
import { DataTableV2DataRow } from '@/catalog-apps/components/component-library/DataTableV2/types'
import { ComponentActionsContainer } from '@/catalog-apps/components/component-library/shared/ComponentActionsContainer'
import { ComponentHeader } from '@/catalog-apps/components/component-library/shared/ComponentHeader'
import { ComponentTitle } from '@/catalog-apps/components/component-library/shared/ComponentTitle'
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/shadcn/charts'
import { cn } from '@/lib/utils'

import { AiAreaChartSchema } from './types-ai-area-chart'

/** Default tilt for category labels in the chat panel so long names fit without overlapping. */
const DEFAULT_X_AXIS_LABEL_ANGLE = -40

/** Matches `--chart-1` ... `--chart-5` on `:root` and `.dark` in `index.scss`. */
const THEME_CHART_COLOR_SLOT_COUNT = 5

const DEFAULT_CATEGORY_SIZE = 56
const DEFAULT_MAX_VISIBLE_CATEGORY_COUNT = 8
const DEFAULT_MIN_CHART_HEIGHT = 280
const DEFAULT_MIN_CHART_WIDTH = 520

function themeChartFillHsl(seriesIndex: number): string {
  const slot = (seriesIndex % THEME_CHART_COLOR_SLOT_COUNT) + 1
  return `hsl(var(--chart-${slot}))`
}

export function AiAreaChart({ schema }: { schema: AiAreaChartSchema }) {
  const xAxisLabelAngle = schema.config.xAxisLabelAngle ?? DEFAULT_X_AXIS_LABEL_ANGLE
  const xAxisTickHeight =
    schema.config.xAxisTickHeight ?? (xAxisLabelAngle !== 0 ? 56 : schema.config.hideXAxis ? 0 : 28)
  const maxVisibleCategoryCount = schema.config.maxVisibleCategoryCount ?? DEFAULT_MAX_VISIBLE_CATEGORY_COUNT
  const categorySize = schema.config.categorySize ?? DEFAULT_CATEGORY_SIZE
  const isScrollable = schema.data.length > maxVisibleCategoryCount
  const chartHeight = schema.config.minChartHeight ?? DEFAULT_MIN_CHART_HEIGHT
  const chartWidth = Math.max(schema.config.minChartWidth ?? DEFAULT_MIN_CHART_WIDTH, schema.data.length * categorySize)

  const exportData = useMemo<DataTableV2DataRow[]>(
    () =>
      schema.data.map((item, index) => {
        const exportRow: DataTableV2DataRow = {
          id: index + 1,
          [schema.config.categoryKey]: item[schema.config.categoryKey],
        }

        schema.config.areaDataKeys.forEach((key) => {
          const seriesLabel = schema.config.seriesConfig[key]?.label ?? key
          exportRow[seriesLabel] = item[key]
        })

        return exportRow
      }),
    [schema]
  )

  return (
    <div className="flex h-[50vh] w-full flex-col overflow-hidden">
      <ComponentHeader emphasize wrapperClassName="rounded-b-none">
        <ComponentTitle title={schema.title} description={schema.description} />
        <ComponentActionsContainer className="bg-transparent p-0">
          <ExportButtonV2
            data={exportData}
            title={schema.title}
            selectionRequired={false}
            className="text-background dark:text-foreground"
          />
        </ComponentActionsContainer>
      </ComponentHeader>
      <div
        className={cn(
          'min-h-0 flex-1 rounded-md border bg-background',
          isScrollable ? 'overflow-auto' : 'overflow-hidden'
        )}
      >
        <ChartContainer
          className={cn('mx-auto aspect-auto min-h-0', isScrollable ? 'justify-start' : 'h-full w-full')}
          config={schema.config.seriesConfig}
          style={
            isScrollable
              ? {
                  height: '100%',
                  minHeight: chartHeight,
                  minWidth: chartWidth,
                  width: chartWidth,
                }
              : undefined
          }
        >
          <AreaChart
            accessibilityLayer
            data={schema.data}
            stackOffset={schema.config.stackOffset}
            margin={{ top: 8, right: 12, bottom: 8, left: 8 }}
          >
            {schema.config.showGrid && <CartesianGrid strokeDasharray="3 3" vertical={false} />}
            <XAxis
              dataKey={schema.config.categoryKey}
              hide={schema.config.hideXAxis}
              orientation={schema.config.xAxisOrientation}
              angle={xAxisLabelAngle}
              textAnchor={xAxisLabelAngle === 0 ? 'middle' : 'end'}
              height={schema.config.hideXAxis ? undefined : xAxisTickHeight}
              interval={isScrollable ? 0 : undefined}
            />
            <YAxis hide={schema.config.hideYAxis} orientation={schema.config.yAxisOrientation} />
            <ChartTooltip cursor={false} content={<ChartTooltipContent indicator="dot" />} />
            {schema.config.showLegend && <ChartLegend content={<ChartLegendContent />} />}
            {schema.config.areaDataKeys.map((key, seriesIndex) => {
              const areaConfig = schema.config.seriesConfig[key as keyof typeof schema.config.seriesConfig]
              const color = areaConfig?.color ?? themeChartFillHsl(seriesIndex)

              return (
                <Area
                  key={key}
                  type={areaConfig?.curveType ?? 'monotone'}
                  dataKey={key}
                  stroke={color}
                  fill={color}
                  fillOpacity={areaConfig?.fillOpacity ?? 0.35}
                  strokeWidth={areaConfig?.strokeWidth ?? 2}
                  connectNulls={areaConfig?.connectNulls ?? false}
                  stackId={
                    schema.config.stacked ? areaConfig?.stackId ?? schema.config.stackId ?? 'stack' : areaConfig?.stackId
                  }
                />
              )
            })}
            {schema.config.referenceLines?.map((referenceLine, index) => (
              <ReferenceLine
                key={`${referenceLine.label || 'ref'}-${referenceLine.y}-${index}`}
                y={referenceLine.y}
                label={referenceLine.label}
                stroke={referenceLine.color || 'hsl(var(--muted-foreground))'}
                strokeDasharray={referenceLine.strokeDasharray || '3 3'}
                ifOverflow={referenceLine.ifOverflow || 'extendDomain'}
              />
            ))}
          </AreaChart>
        </ChartContainer>
      </div>
    </div>
  )
}
